import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as PhosphorIcons from "@phosphor-icons/react";
import { CaretLeft, FilePdf, FileDoc, CheckSquare, Square, MapPin, User } from "@phosphor-icons/react";
import { api, fileUrl } from "../lib/api";

function SectionIcon({ name, ...props }) {
  const Icon = PhosphorIcons[name] || PhosphorIcons.Circle;
  return <Icon {...props} />;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString();
}

function Check({ on, label }) {
  return (
    <div className="flex items-center gap-2 py-1">
      {on ? (
        <CheckSquare size={20} weight="fill" className="text-teal shrink-0" />
      ) : (
        <Square size={20} weight="bold" className="text-lineheavy shrink-0" />
      )}
      <span className={on ? "text-navydark" : "text-steel/60"}>{label}</span>
    </div>
  );
}

function ItemView({ item, data, photos }) {
  const v = data?.[item.id];
  switch (item.type) {
    case "info":
      return null;
    case "text":
    case "textarea":
    case "weather":
    case "weather_crash":
    case "select":
      if (!v) return null;
      return (
        <div>
          <p className="uc-label">{item.label}</p>
          <p className="text-navydark whitespace-pre-line">{v}</p>
        </div>
      );
    case "datetime":
      if (!v) return null;
      return (
        <div>
          <p className="uc-label">{item.label}</p>
          <p className="text-navydark">{fmtDateTime(v)}</p>
        </div>
      );
    case "gps":
      if (!v || v.lat == null) return null;
      return (
        <div>
          <p className="uc-label">{item.label}</p>
          <p className="text-navydark inline-flex items-center gap-1">
            <MapPin size={16} weight="bold" className="text-teal" />
            {v.lat.toFixed(5)}, {v.lng.toFixed(5)} (±{Math.round(v.accuracy)}m)
          </p>
        </div>
      );
    case "checkbox":
      if (!v?.checked) return null;
      return <Check on label={item.label + (v.note ? ` — ${v.note}` : "")} />;
    case "group": {
      const children = v?.children || {};
      const any = Object.values(children).some(Boolean) || v?.note;
      if (!any) return null;
      return (
        <div>
          <p className="font-head font-semibold text-navy mb-1">{item.label}</p>
          {item.children.filter((c) => children[c.id]).map((c) => (
            <Check key={c.id} on label={c.label} />
          ))}
          {v?.note && <p className="text-sm text-steel italic mt-1">Note: {v.note}</p>}
        </div>
      );
    }
    case "photos":
      if (!photos || photos.length === 0) return null;
      return (
        <div>
          <p className="uc-label">Photos ({photos.length})</p>
          <div className="grid grid-cols-2 gap-3">
            {photos.map((p, i) => (
              <figure key={p.fileId || i} className="rounded-xl overflow-hidden border-2 border-line">
                {p.fileId && (
                  <img src={fileUrl(p.fileId)} alt={p.caption || "photo"} className="w-full h-40 object-cover" />
                )}
                {p.caption && <figcaption className="p-2 text-sm text-steel">{p.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function AdminInspectionView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [insp, setInsp] = useState(null);
  const [schema, setSchema] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [ins, sch] = await Promise.all([
          api.get(`/admin/inspections/${id}`),
          api.get("/schema"),
        ]);
        setInsp(ins.data);
        setSchema(sch.data.sections);
      } catch (e) {
        setNotFound(true);
      }
    })();
  }, [id]);

  async function exportReport(format) {
    setExporting(true);
    try {
      const res = await api.get(`/admin/inspections/${id}/export`, {
        params: { format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (insp.title || "crash-inspection").replace(/[^a-z0-9]+/gi, "_");
      a.download = `${safe}.${format === "docx" ? "docx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  }

  if (notFound)
    return (
      <div className="p-10 text-center">
        <p className="text-steel mb-4">Inspection not found.</p>
        <button className="uc-btn uc-btn-primary" onClick={() => nav("/admin")}>Back</button>
      </div>
    );
  if (!insp || !schema) return <div className="p-10 text-steel">Loading…</div>;

  return (
    <div className="min-h-screen bg-paper pb-10">
      <header className="sticky top-0 z-20 bg-white border-b-2 border-line">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="admin-view-back-btn"
            onClick={() => nav("/admin")}
            className="w-11 h-11 rounded-full border-2 border-line flex items-center justify-center text-navy shrink-0"
            aria-label="Back"
          >
            <CaretLeft size={22} weight="bold" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="font-head font-semibold text-navydark truncate">{insp.title || "Untitled inspection"}</p>
            <p className="text-sm text-steel truncate inline-flex items-center gap-1">
              <User size={13} weight="bold" /> {insp.owner?.name || insp.owner?.email}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex gap-3 mb-6">
          <button
            data-testid="admin-view-export-pdf"
            disabled={exporting}
            onClick={() => exportReport("pdf")}
            className="uc-btn uc-btn-primary flex-1"
          >
            <FilePdf size={20} weight="bold" /> PDF
          </button>
          <button
            data-testid="admin-view-export-docx"
            disabled={exporting}
            onClick={() => exportReport("docx")}
            className="uc-btn uc-btn-teal flex-1"
          >
            <FileDoc size={20} weight="bold" /> Word
          </button>
        </div>

        <div className="space-y-6">
          {schema.map((section) => {
            return (
              <div key={section.id} className="bg-white border-2 border-line rounded-2xl overflow-hidden">
                <div className="bg-navy text-white px-4 py-3 flex items-center gap-2">
                  <SectionIcon name={section.icon} size={20} weight="bold" />
                  <span className="font-head font-semibold">{section.title}</span>
                </div>
                <div className="p-4 space-y-4">
                  {section.items.map((item) => (
                    <ItemView key={item.id} item={item} data={insp.data} photos={insp.photos} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
